import { dealerMonitorEquipment } from '@racepointenergy/swc/scs/dealer-monitor-equipment'
import { equipmentEntityAdapter } from '@racepointenergy/swc/scs/features/host-equipment'
import { HomeId } from '@racepointenergy/swc/scs/types'
import { useMemo, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'

import { Spinner } from 'components/Loader'
import { RadioTiles } from 'components/RadioTiles'
import { Select } from 'components/Select'
import { CommonSearch } from 'components/Table'
import { H2 } from 'components/Typography'

import SysMonGuard, { GuardRouterProps } from '../../../SysMonGuard'
import { SpinnerContainer } from '../../HomeStatus/SubInfo/ConnectedApps/styles'
import HomeDevicesTable from '../DevicesTable'
import { homeDevicesMessages } from '../messages'
import {
  HomeDevicesContainer,
  HomeDevicesCount,
  HomeDevicesFiltersWrapper,
  HomeDevicesHeader,
  HomeDevicesRedioTileWrapper,
  HomeDevicesSelectorsWrapper,
  HomeDevicesTitleWrapper,
} from '../styles'
import { Devices } from '../types'
import { useUiDevices } from '../hooks'
import { getUniqueDevicesValues } from '../utils'
import { createDropdownOption } from 'features/home/utils'

type FormatMessageType = ReturnType<typeof useIntl>['formatMessage']

interface HomeDevicesProps {
  homeId: HomeId
}

const equipmentSelectors = equipmentEntityAdapter.getSelectors()

const equipmentInitialState = equipmentEntityAdapter.getInitialState()

enum FILTER_BUTTONS_VALUES {
  ALL = 'all',
  SAVANT = 'savant',
  USER = 'user',
}

const FILTER_BUTTONS = (format: FormatMessageType) => [
  {
    label: format(homeDevicesMessages.filterAll),
    value: FILTER_BUTTONS_VALUES.ALL,
    defaultChecked: true,
  },
  {
    label: format(homeDevicesMessages.filterSavantOnly),
    value: FILTER_BUTTONS_VALUES.SAVANT,
  },
  {
    label: format(homeDevicesMessages.filterUserInterfaces),
    value: FILTER_BUTTONS_VALUES.USER,
  },
]

const defaultRoomFilter = createDropdownOption('All Rooms', '')
const defaultStatusFilter = createDropdownOption('Any Status', '')
const defaultDeviceFilter = createDropdownOption('All Devices', '')

function HomeDevices({ homeId: id }: HomeDevicesProps) {
  const { formatMessage } = useIntl()
  const [searchValue, setSearchValue] = useState<string>()
  const [room, setRoom] = useState(defaultRoomFilter)
  const [status, setStatus] = useState(defaultStatusFilter)
  const [deviceType, setDeviceType] = useState(defaultDeviceFilter)

  const [selectedRadioFilter, setSelectedRadioFilter] = useState(
    FILTER_BUTTONS_VALUES.ALL
  )

  const { data = equipmentInitialState, isFetching } =
    dealerMonitorEquipment.useQuery({ id })

  const { additionalDevices } = useUiDevices(id)

  const tableData: Devices[] = useMemo(
    () => [
      ...equipmentSelectors.selectAll(data).map((process) => ({
        statusPath: process.StatusPath,
        status: process.Status,
        deviceName: process.Name || process.UniqueID || '-',
        deviceType: process.DeviceType || process.ChassisDeviceType || '-',
        controller: process.ControllerName ?? '-',
        room: process.configPlacedZone ?? '-',
        ipAddress: process.Hostname || process.IPAddress,
        deviceManufacturer: process.DeviceManufacturer,
      })),
      ...additionalDevices,
    ],
    [additionalDevices, data]
  )

  const memorizedFilteredData = useMemo(() => {
    if (selectedRadioFilter === FILTER_BUTTONS_VALUES.SAVANT)
      return tableData.filter((e) => e?.deviceManufacturer === 'Savant')
    else if (selectedRadioFilter === FILTER_BUTTONS_VALUES.USER)
      return additionalDevices
    return tableData
  }, [selectedRadioFilter, tableData, additionalDevices])

  const filterOptions = useMemo(
    () => getUniqueDevicesValues(tableData),
    [tableData]
  )

  const headers = useMemo(
    () => ({
      status: {
        label: formatMessage(homeDevicesMessages.statusColumn),
        id: 'status',
      },
      deviceName: {
        label: formatMessage(homeDevicesMessages.deviceNameColumn),
        id: 'deviceName',
      },
      deviceType: {
        label: formatMessage(homeDevicesMessages.deviceTypeColumn),
        id: 'deviceType',
      },
      controller: {
        label: formatMessage(homeDevicesMessages.controllerColumn),
        id: 'controller',
      },
      room: {
        label: formatMessage(homeDevicesMessages.roomColumn),
        id: 'room',
      },
      ipAddress: {
        label: formatMessage(homeDevicesMessages.ipAddressColumn),
        id: 'ipAddress',
      },
      manufacturer: {
        label: formatMessage(homeDevicesMessages.manufacturerColumn),
        id: 'manufacturer',
      },
    }),
    [formatMessage]
  )

  const filteredDevices = useMemo(() => {
    let data = memorizedFilteredData

    if (room.value)
      data = data.filter((deviceItem) => deviceItem.room === room.value)
    if (status.value)
      data = data.filter(
        (deviceItem) => `${deviceItem.status}` === status.value
      )
    if (deviceType.value)
      data = data.filter(
        (deviceItem) => deviceItem.deviceType === deviceType.value
      )
    return data
  }, [deviceType.value, memorizedFilteredData, room.value, status.value])

  if (isFetching) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  }

  return (
    <HomeDevicesContainer>
      <HomeDevicesHeader>
        <HomeDevicesFiltersWrapper>
          <HomeDevicesTitleWrapper>
            <H2>{formatMessage(homeDevicesMessages.title)}</H2>
            <HomeDevicesCount>
              {memorizedFilteredData.length}&nbsp;
              {formatMessage(homeDevicesMessages.countItems)}
            </HomeDevicesCount>
          </HomeDevicesTitleWrapper>
          <HomeDevicesRedioTileWrapper>
            <RadioTiles
              onChange={setSelectedRadioFilter}
              options={FILTER_BUTTONS(formatMessage)}
              name="filter"
            />
          </HomeDevicesRedioTileWrapper>
        </HomeDevicesFiltersWrapper>

        <HomeDevicesSelectorsWrapper>
          <Select
            name={formatMessage(homeDevicesMessages.roomDropdownPlaceholder)}
            value={room}
            onChange={setRoom}
            options={filterOptions?.rooms}
            placeholder={formatMessage(
              homeDevicesMessages.roomDropdownPlaceholder
            )}
          />
          <Select
            name={formatMessage(
              homeDevicesMessages.deviceTypeDropdownPlaceholder
            )}
            value={deviceType}
            onChange={setDeviceType}
            options={filterOptions?.deviceTypes}
            placeholder={formatMessage(
              homeDevicesMessages.deviceTypeDropdownPlaceholder
            )}
          />
          <Select
            name={formatMessage(homeDevicesMessages.statusDropdownPlaceholder)}
            value={status}
            onChange={setStatus}
            options={filterOptions?.statuses}
            placeholder={formatMessage(
              homeDevicesMessages.statusDropdownPlaceholder
            )}
          />
          <CommonSearch
            setSearchValue={setSearchValue}
            placeholder={formatMessage(homeDevicesMessages.searchPlaceholder)}
          />
        </HomeDevicesSelectorsWrapper>
      </HomeDevicesHeader>

      <HomeDevicesTable
        headers={headers}
        devices={filteredDevices}
        searchValue={searchValue}
      />
    </HomeDevicesContainer>
  )
}

const HomeDevicesGuard = (props: GuardRouterProps) => {
  const { homeId } = props.match.params
  return (
    <SysMonGuard
      homeId={homeId}
      title={<FormattedMessage {...homeDevicesMessages.title} />}
    >
      <HomeDevices homeId={homeId} />
    </SysMonGuard>
  )
}

export default HomeDevicesGuard
